import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CalendarDays, Users } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { AdminNav } from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none placeholder:text-soft/50 focus:border-clay transition-colors duration-300";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return iso;
  }
};

const StatusPill = ({ status }) => {
  const styles = {
    paid: "border-clay/40 bg-clay/10 text-clay",
    pending: "border-ink/20 bg-parchment text-soft",
    cancelled: "border-rust/30 bg-rust/5 text-rust",
  };
  return (
    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] tracking-[0.12em] uppercase ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

export default function AdminBookings() {
  useSeo({ title: "Bookings", noindex: true });
  const [token, setToken] = useState(localStorage.getItem("wd-admin") || "");
  const [groups, setGroups] = useState(null);
  const [withdrawals, setWithdrawals] = useState(null);
  const [handlingId, setHandlingId] = useState(null);
  const [error, setError] = useState(null);
  const [includeUnpaid, setIncludeUnpaid] = useState(false);

  const load = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { headers: { "X-Admin-Token": t } });
      setGroups(data.groups || []);
      setError(null);
      try {
        const wd = await axios.get(`${API}/admin/withdrawals`, { headers: { "X-Admin-Token": t } });
        setWithdrawals(wd.data.withdrawals || []);
      } catch { /* withdrawals view is optional */ }
    } catch {
      setGroups(null);
      setError("That key didn't work — check it and try again.");
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, []);

  const markHandled = async (id) => {
    setHandlingId(id);
    try {
      await axios.patch(`${API}/admin/withdrawals/${id}`, { status: "handled" }, { headers: { "X-Admin-Token": token } });
      setWithdrawals((ws) => ws.map((w) => (w.id === id ? { ...w, status: "handled", handled_at: new Date().toISOString() } : w)));
    } catch {
      toast.error("Couldn't update the status — please try again.");
    } finally {
      setHandlingId(null);
    }
  };

  const saveToken = (t) => {
    setToken(t);
    localStorage.setItem("wd-admin", t);
    setError(null);
    if (t) load(t);
  };

  const visibleBookings = (g) =>
    includeUnpaid ? g.bookings : g.bookings.filter((b) => b.payment_status === "paid");
  const visibleGroups = (groups || [])
    .map((g) => ({ ...g, shown: visibleBookings(g) }))
    .filter((g) => g.shown.length > 0);

  return (
    <main className="bg-cream min-h-screen" data-testid="admin-bookings-page">
      <section className="mx-auto max-w-3xl px-6 pt-40 pb-24">
        <div className="flex items-center gap-3 text-clay">
          <CalendarDays size={20} strokeWidth={1.25} />
          <p className="text-xs tracking-[0.35em] uppercase">Workshops &amp; circles</p>
        </div>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tight text-ink">Bookings</h1>
        <p className="mt-4 text-base leading-relaxed text-soft">
          {groups === null
            ? "Enter your key to see who is coming."
            : "Paid bookings by workshop and date. Capacity counts paid places only."}
        </p>

        <div className="mt-8">
          <AdminNav current="/admin/bookings" />
        </div>

        <div className="mt-10 max-w-xs">
          <label htmlFor="adm-token" className="text-xs tracking-[0.25em] uppercase text-soft">Your key</label>
          <input id="adm-token" type="password" value={token} onChange={(e) => saveToken(e.target.value)}
            placeholder="Admin key" className={inputCls} data-testid="bookings-token-input" />
          {error && <p className="mt-2 text-sm text-rust" data-testid="bookings-error">{error}</p>}
        </div>

        {groups !== null && (
          <label className="mt-10 inline-flex cursor-pointer items-center gap-3 text-sm text-soft" data-testid="bookings-toggle-wrap">
            <input type="checkbox" checked={includeUnpaid} onChange={(e) => setIncludeUnpaid(e.target.checked)}
              className="h-4 w-4 accent-[#B4756A]" data-testid="bookings-include-unpaid-toggle" />
            Also show pending &amp; cancelled checkouts
          </label>
        )}

        {groups !== null && visibleGroups.length === 0 && (
          <p className="mt-12 font-serif italic text-lg text-soft" data-testid="bookings-empty">
            No {includeUnpaid ? "" : "paid "}bookings yet.
          </p>
        )}

        <div className="mt-12 space-y-14">
          {visibleGroups.map((g) => (
            <motion.section key={`${g.offer}-${g.date}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`booking-group-${g.offer}-${g.date}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-ink/15 pb-4">
                <div>
                  <h2 className="font-serif text-2xl tracking-tight text-ink">{g.topic}</h2>
                  <p className="mt-1 text-xs tracking-[0.2em] uppercase text-soft">{fmtDate(g.date)}</p>
                </div>
                {g.capacity != null && (
                  <p className="flex items-center gap-2 text-sm text-soft" data-testid={`booking-capacity-${g.offer}-${g.date}`}>
                    <Users size={14} strokeWidth={1.5} />
                    <span>
                      <strong className="font-medium text-ink">{g.paid_places} of {g.capacity}</strong> places booked
                      · <strong className="font-medium text-ink">{g.remaining}</strong> available
                    </span>
                  </p>
                )}
              </div>
              <ul className="divide-y divide-ink/8">
                {g.shown.map((b, i) => (
                  <li key={`${b.email}-${i}`} className="flex flex-wrap items-center gap-x-6 gap-y-1 py-4"
                    data-testid={`booking-row-${g.offer}-${g.date}-${i}`}>
                    <div className="min-w-[180px] flex-1">
                      <p className="text-base text-ink">{b.name}</p>
                      <p className="text-sm text-soft">{b.email}</p>
                    </div>
                    <p className="text-sm capitalize text-soft">{b.ticket}{b.places > 1 ? ` · ${b.places} places` : ""}</p>
                    {b.amount != null && (
                      <p className="text-sm text-soft">€{Number(b.amount).toFixed(0)}</p>
                    )}
                    <StatusPill status={b.payment_status} />
                  </li>
                ))}
              </ul>
            </motion.section>
          ))}
        </div>

        {withdrawals !== null && (
          <section className="mt-20" data-testid="admin-withdrawals">
            <div className="flex items-center gap-3 text-clay">
              <p className="text-xs tracking-[0.35em] uppercase">Withdrawal requests</p>
            </div>
            {withdrawals.length === 0 ? (
              <p className="mt-6 font-serif italic text-lg text-soft" data-testid="withdrawals-empty">No withdrawal requests.</p>
            ) : (
              <ul className="mt-6 divide-y divide-ink/10 border-t border-ink/15">
                {withdrawals.map((w, i) => (
                  <li key={w.id} className="py-4" data-testid={`withdrawal-row-${i}`}>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                      <p className="text-base text-ink">{w.name}</p>
                      <p className="text-sm text-soft">{w.email}</p>
                      <p className="text-xs tracking-[0.15em] uppercase text-clay">{w.service}</p>
                      <span
                        className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] tracking-[0.12em] uppercase ${
                          w.status === "handled" ? "border-sage/40 bg-sage/10 text-sage" : "border-clay/40 bg-clay/10 text-clay"
                        }`}
                        data-testid={`withdrawal-status-${i}`}
                      >
                        {w.status === "handled" ? "Handled" : "Open"}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-1">
                      <p className="text-sm text-soft">
                        {w.purchase ? `Purchase: ${w.purchase.topic} — ${w.purchase.date} · ` : ""}
                        Requested {String(w.created_at).replace("T", " ").slice(0, 16)} UTC · ref {w.id.slice(0, 8)}
                      </p>
                      {w.status !== "handled" && (
                        <button
                          type="button"
                          onClick={() => markHandled(w.id, i)}
                          disabled={handlingId === w.id}
                          data-testid={`withdrawal-handle-${i}`}
                          className="rounded-full border border-ink/20 px-4 py-1 text-xs tracking-[0.15em] uppercase text-soft transition-colors duration-300 hover:border-clay hover:text-ink disabled:opacity-50"
                        >
                          Mark handled
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </section>
    </main>
  );
}
